import { Strong } from '@radix-ui/themes'
import { type Fetcher, type Location } from '@remix-run/react'
import { type TFunction } from 'i18next'
import * as React from 'react'

import { StringToComponent } from '#app/components/common/string-to-component.tsx'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { useNotify } from '#app/utils/hooks/use-notify.ts'

/* -------------------------------------------------------------------------------------------------
 * useCartSetQuantityNotify
 * -----------------------------------------------------------------------------------------------*/

type UseCartSetQuantityNotifyProps = {
	setApp: React.Dispatch<React.SetStateAction<App>>
	setQuantityFetcher: Fetcher
	t: TFunction<string, undefined>
	name: string
	location: Location
}

const useCartSetQuantityNotify = ({
	setApp,
	setQuantityFetcher,
	t,
	name,
	location,
}: UseCartSetQuantityNotifyProps) =>
	useNotify({
		...setQuantityFetcher,
		same: !location.pathname.includes('/checkout'),
		setApp,
		translations: {
			success: {
				title: t('notify.setCartItemQuantity.success.title'),
				description: (
					<StringToComponent
						text={t('notify.setCartItemQuantity.success.description-$val')}
					>
						<Strong>{name}</Strong>
					</StringToComponent>
				),
			},
			error: {
				title: t('notify.setCartItemQuantity.error.title'),
				description: (
					<StringToComponent
						text={'notify.setCartItemQuantity.error.description-$val'}
					>
						<Strong>{name}</Strong>
					</StringToComponent>
				),
			},
		},
	})

/* -------------------------------------------------------------------------------------------------
 * useCartRemoveItemNotify
 * -----------------------------------------------------------------------------------------------*/

type UseCartRemoveItemNotifyProps = {
	setApp: React.Dispatch<React.SetStateAction<App>>
	removeItemFetcher: Fetcher
	t: TFunction<string, undefined>
	name: string
	location: Location
}

const useCartRemoveItemNotify = ({
	setApp,
	removeItemFetcher,
	t,
	name,
	location,
}: UseCartRemoveItemNotifyProps) =>
	useNotify({
		...removeItemFetcher,
		same: !location.pathname.includes('/checkout'),
		setApp,
		translations: {
			success: {
				title: t('notify.removeCartItem.success.title'),
				description: (
					<StringToComponent
						text={t('notify.removeCartItem.success.description-$val')}
					>
						<Strong>{name}</Strong>
					</StringToComponent>
				),
			},
			error: {
				title: t('notify.removeCartItem.error.title'),
				description: (
					<StringToComponent
						text={t('notify.removeCartItem.error.description-$val')}
					>
						<Strong>{name}</Strong>
					</StringToComponent>
				),
			},
		},
	})

/* -----------------------------------------------------------------------------------------------*/

export {
	type UseCartSetQuantityNotifyProps,
	type UseCartRemoveItemNotifyProps,
	useCartSetQuantityNotify,
	useCartRemoveItemNotify,
}
